/**
 * Componente de sección de Dashboard con comparativas
 * Dashboard section component with comparisons
 */

import { useTranslation } from 'react-i18next';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { ComparisonChart } from './ComparisonChart';
import {
  FilesComparisonResponse,
  WebhooksComparisonResponse,
  ApiComparisonResponse,
} from '../../models/TrackingApiResponse';

interface DashboardSectionProps {
  filesComparison?: FilesComparisonResponse;
  webhooksComparison?: WebhooksComparisonResponse;
  apiComparison?: ApiComparisonResponse;
  isLoading?: boolean;
}

const AnomaliesList = ({ anomalies, t }: { anomalies: any[]; t: any }) => {
  if (anomalies.length === 0) return null;

  return (
    <div className="mt-6 pt-6 border-t border-card-border">
      <h3 className="text-sm font-semibold text-title mb-4">{t('dashboard.anomalies.title')}</h3>
      <div className="space-y-3">
        {anomalies.map((anomaly, index) => (
          <div
            key={index}
            className="bg-card-bg border border-card-border rounded-lg p-4 hover:border-primary/50 transition-colors"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-title">{anomaly.hour}</span>
              <span
                className={`text-sm font-semibold ${
                  Math.abs(anomaly.difference_percent) > 50 ? 'text-error' : 'text-warning'
                }`}
              >
                {anomaly.difference_percent > 0 ? '+' : ''}
                {anomaly.difference_percent.toFixed(1)}%
              </span>
            </div>
            <div className="flex gap-4 text-xs text-complementary-text">
              <span>
                {t('dashboard.anomalies.current')}: <span className="text-text font-medium">{anomaly.current}</span>
              </span>
              <span>
                {t('dashboard.anomalies.previous')}: <span className="text-text font-medium">{anomaly.previous}</span>
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const DashboardSection = ({
  filesComparison,
  webhooksComparison,
  apiComparison,
  isLoading = false,
}: DashboardSectionProps) => {
  const { t } = useTranslation('trackingModule');

  return (
    <div className="space-y-6" data-testid="dashboard-section">
      {/* Archivos */}
      <SectionBox>
        <h2 className="text-lg font-semibold text-title mb-4">{t('dashboard.tabs.files')}</h2>
        <ComparisonChart
          currentData={filesComparison?.current_period?.data || []}
          previousData={filesComparison?.previous_period?.data || []}
          type="files"
          isLoading={isLoading}
        />
        <AnomaliesList anomalies={filesComparison?.anomalies || []} t={t} />
      </SectionBox>

      {/* Webhooks */}
      <SectionBox>
        <h2 className="text-lg font-semibold text-title mb-4">{t('dashboard.tabs.webhooks')}</h2>
        <ComparisonChart
          currentData={webhooksComparison?.current_period?.data || []}
          previousData={webhooksComparison?.previous_period?.data || []}
          type="webhooks"
          isLoading={isLoading}
        />
        <AnomaliesList anomalies={webhooksComparison?.anomalies || []} t={t} />
      </SectionBox>

      {/* API Calls */}
      <SectionBox>
        <h2 className="text-lg font-semibold text-title mb-4">{t('dashboard.tabs.api')}</h2>
        <ComparisonChart
          currentData={apiComparison?.current_period?.data || []}
          previousData={apiComparison?.previous_period?.data || []}
          type="api"
          isLoading={isLoading}
        />
        <AnomaliesList anomalies={apiComparison?.anomalies || []} t={t} />
      </SectionBox>
    </div>
  );
};
